import React, { memo } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { format, sub } from 'date-fns';
import { AnimatePresence } from "framer-motion"
import { isMobile } from 'react-device-detect'
import moment from 'moment-timezone'

import { Check, RichText, Button, PopOut, Schedule, Property, Animation, Icon } from 'components'
import { ItemOptions, ItemInput, ItemModal, TagBlock, ToggleButton, ScheduleInput } from 'features'
import { itemContext, sectionContext, tagContext, clientContext } from 'context'
import { FaAngleDown, FaAngleRight, FaEllipsisH, FaExpandAlt, FaCalendar, FaCheckDouble, FaAngleUp, FaGripVertical } from 'react-icons/fa'
import ItemProperties from './ItemProperties'
import ClientTooltip from './ClientTooltip'

import { scheduleString, getInputID } from 'utils'
import * as actionCreators from 'store/actions';
import { Alert, Item } from 'schema';
import classes from './ItemBlock.module.css'

const TextContainer = styled.div`
    position: relative;
    padding: ${props => props.hasProperties ? '0.5rem 0.5rem 0 0' : '0.5rem 0.5rem 0.3rem 0'};
    z-index: 0;
    width: 100%;
    height: min-height;
    overflow: visible;
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal;
    color: ${props => props.completed ? 'var(--secondary-accent-color)' : 'var(--text-color)'};
    display: flex;
    transition: all 0.3s cubic-bezier(.55, 0, .1, 1);
    /* border: 1px solid red; */
    @media not all and (min-resolution:.001dpcm) { 
        -webkit-text-decoration-line: ${props => props.completed ? 'line-through' : null};
    };
    @media screen and (-webkit-min-device-pixel-ratio:0) and (min-resolution:.001dpcm) {
        text-decoration: ${props => props.completed ? 'line-through' : null};
        text-decoration-color: var(--secondary-accent-color);
    };
    
`

const Divider = styled.div`
    border-style: solid;
    border-width: 1px 0 0 0;
    border-color: ${props => props.color || 'var(--secondary-color)'};
`;

const Wrapper = styled.div`
    border-radius: 5px;
    border: ${props => props.focus ? '2px solid var(--focus-color)' : '2px solid transparent'};
`

const defaultStyle = {
    primaryColor: 'var(--primary-color)',
    secondaryColor: 'var(--secondary-color)'
}

const ItemBlock = props => {
    const {
        itemID,
        // item,
        styles,
        onSelect,
        focus,
        disableDrag,
        disableButtons,
        disableToggle,
        pageName,
        type,
        primaryColor,
        secondaryColor,
        maxLines,
        isDragging,
        edit,
        onCheck,
        showDivider=false
    } = props
    const dispatch = useDispatch()
    const updatedStyles = Object.assign({}, defaultStyle, styles)
    const secondaryStyles = {
        color:'var(--secondary-accent-color)',
        backgroundColor: primaryColor || 'var(--primary-color)',
        hoverColor:'var(--text-color)',
        margin:'0',
        padding:'0.3rem',
        fontSize:'0.8rem',
        fontWeight: 'lighter',
        boxShadowColor: 'transparent'
    }

    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const user = useSelector(state => state.auth.user)
    const inputID = useSelector(state => state.content.inputID)
    const sessionId = useSelector(state => state.content.sessionId)
    const item = new Item(data.items[itemID])
    const section = item && data.sections[item['sectionID']]

    const getComponents = () => {
        let output = {
            primaryButtons: null,
            secondaryButtons: null,
            text: null,
            properties: null
        }

        if(!item){ return output }

        // Constants
        const edit = `ItemInput_${item['itemID']}` === inputID
        const section = data['sections'][item['sectionID']]
        const completed = item['status'] === itemContext.STATUS.COMPLETED | (section && section.status === sectionContext.STATUS.ARCHIVE)

        // 1. Primary buttons
        const hasChildren = item.children['INCOMPLETE'] ? Object.values(item.children['INCOMPLETE']).length > 0 : false
        const showMore = item['children_visibility']
        output['primaryButtons'] =
        <div className={classes.PrimaryButtons}>
                {!disableDrag && <div className={classes.DragHandle}><FaGripVertical/></div>}
                <Check disabled={disableButtons}
                    filled={!!completed}
                    color={!completed ? itemContext.PRIORITY_COLOR[item.priority.toUpperCase()] : null}
                    backgroundColor={completed ? itemContext.PRIORITY_COLOR[item.priority.toUpperCase()] : null}
                    borderColor={itemContext.PRIORITY_COLOR[item.priority.toUpperCase()]}
                    onSelect={onToggleStatus}/>
            </div>


        // 2. SecondaryButtons
        output['secondaryButtons'] = 
        <div className={classes.SecondaryButtons}>
            {disableButtons || disableToggle || type === 'detailed' ? null
                : !disableButtons & hasChildren & type !== 'KANBAN' ? <ToggleButton open={showMore} onSelect={onToggleChildren} styles={{backgroundColor: primaryColor || 'var(--primary-color)', padding: '0.3rem', fontSize: '1rem', color:'var(--secondary-accent-color)'}} icon={<FaAngleUp/>} end={180}/>
                : null}
            <Button icon={<FaEllipsisH/>} contextMenu={<ItemOptions item={item}/>} styles={secondaryStyles} placement='bottom-end'/>
        </div>

        // 3. Properties
        let properties = []

        // 3a. Page name (for Search.js)
        if(pageName && data.pages[item['pageID']]){
            const page = data.pages[item['pageID']]
            const pageTitle =
            <div className={classes.PageTitle}>
                <Icon size={'1rem'} value={page.icon ? page.icon.value : null} type={page.icon ? page.icon.type : null} styles={{padding: 0}}/>
                {page['page_name']}
            </div>
            properties.push(
                <Property 
                    // key={uuidv4()} 
                    label={pageTitle}
                    />
            )
        }

        // 3b. Number of items
        const familyCount = item.familyCount(true, data)
        if(familyCount.total > 0){
            properties.push(
                <Property 
                    // key={uuidv4()}
                    tooltip={showMore ? 'Hide sub-tasks' : 'Show sub-tasks'}
                    onSelect={onToggleChildren}
                    label={`${familyCount['completed']}/${familyCount['total']}`}/>
            )
        }

        // 3c. Client item label
        if(item['client']){
            properties.push(
                <Property 
                    // key={uuidv4()}
                    img={clientContext[item['client']]['icon']['value']}
                    tooltip={item['clientTitle'] && <ClientTooltip {...item['clientTitle']}/>}/>
            )
        }

        // 3d. Schedule
        const itemDates = {
            start_date: item['start_date'],
            end_date: item['end_date'],
            start_time: item['start_time'],
            end_time: item['end_time']
        }
        const schedule = scheduleString(itemDates, user['timezone']);
        const timezone = user['timezone']
        if(schedule['valid']){
            const dates = {
                start_date: item.start_date ? moment(item.start_date).tz(timezone) : null,
                end_date: item.end_date ? moment(item.end_date).tz(timezone) : null, 
                start_time: item.start_time ? moment(item.start_time).tz(timezone) : null,
                end_time: item.end_time ? moment(item.end_time).tz(timezone) : null
            }
            properties.push(
                <Property 
                    // key={uuidv4()} 
                    icon={<FaCalendar/>} tooltip='Schedule' 
                    label={<Schedule dates={{start: schedule.start, end: schedule.end}}/>}
                    contextMenu={!disableButtons && <ScheduleInput onSubmit={onSetSchedule} 
                                style={{backgroundColor:'var(--secondary-color)',padding:'1rem', width: 'fit-content', opacity:1}}
                                schedule={dates}/>}/>
            )
        }

        // 3e. Tags
        if(item['tags']){
            Object.values(item['tags']).forEach(tagID => {
                const tag = data.tags[tagID]
                tag && properties.push(
                    <TagBlock 
                        key={tagID}
                        tag={tag} contextMenu={!disableButtons}
                        style={{margin: '0.2rem'}}/>
                )
            })
        }

        output['properties'] = properties

        // 4. Text
        const itemInputID = `ItemInput_${item['itemID']}`
        output['text'] = 
        itemInputID === inputID & type !== 'SEARCH'
        ? <TextContainer edit={itemInputID === inputID} hasProperties={properties.length > 0}>
            <ItemInput styles={styles}
                disableProps={type==='detailed'} 
                disableButtons={disableButtons} 
                id={`ItemInput_${item['itemID']}`} 
                item={item} 
                pageID={item['pageID']} 
                sectionID={item['sectionID']} 
                type='Edit' 
                onClose={onToggleEdit}/>
        </TextContainer>
        : <TextContainer hasProperties={properties.length > 0}
            // cursor={onSelect && 'pointer'} 
            edit={itemInputID=== inputID} 
            completed={completed} 
            // onClick={() => console.log('[ItemBlock]', {completed, type})}
            onClick={() => completed | ['VIEW','VIEW2'].includes(type) ? dispatch(actionCreators.setModal(<ItemModal itemID={item['itemID']}/>, `${item['itemID']}_ItemModal`))
                        : isMobile || disableButtons || item['status'] === itemContext.STATUS.COMPLETED ? null
                        : dispatch(actionCreators.setInput(`ItemInput_${item['itemID']}`))}
            >
            {/* <div>{item['itemID']}</div> */}
            <RichText id={itemInputID} 
                // key={`${item['itemID']}_RichText${uuidv4()}`} 
                value={item['content']} 
                readOnly disableAutoUpdate maxLines={maxLines} 
                styles={{
                    primaryColor,
                    secondaryColor,
                    padding: 0,
                    margin: '0 0 0 1px'
                }}/>
        </TextContainer>

        return output
    }

    const onToggleStatus = () => {
        dispatch(actionCreators.updateItemStatus(item, data, user['uid'], sessionId))
        onCheck && onCheck()
    }

    const onToggleChildren = () => {
        let updatedItem = {...item}
        updatedItem['children_visibility'] = !updatedItem['children_visibility']   
        dispatch(actionCreators.updateItem(updatedItem, data, user['uid'], sessionId))
    }

    const onToggleEdit = () => {
        actionCreators.setInput(`ItemInput_${itemID}`)
    }

    const onSetSchedule = (schedule) => {
        let updatedItem = {...item}
        const hasSchedule = schedule['startDate'] || schedule['endDate'] || schedule['startTime'] || schedule['endTime'] ? true : false
        const refDate = hasSchedule && new Date(schedule['startTime'] || schedule['endTime'] || schedule['startDate'] || schedule['endDate'])

        // Add schedule
        updatedItem['start_date'] = schedule['startDate']
        updatedItem['end_date'] = schedule['endDate']
        updatedItem['start_time'] = schedule['startTime']
        updatedItem['end_time'] = schedule['endTime']

        let updatedAlerts = []
        if(hasSchedule & updatedItem['alerts'].length > 0){
            // Update existing alerts
            Object.values(updatedItem['alerts']).forEach(alertID => {
                let updatedAlert = data['alerts'][alertID]
                let option = {}
                option[updatedAlert['reference']['metric']] = updatedAlert['reference']['value']
                const date = sub(new Date(refDate), option)
                const type = date.getHours() === 0 & date.getMinutes() === 0 & date.getSeconds() === 0 & date.getMilliseconds() === 0 ? 'day' : 'time'
                const subtitle = format(date, type === 'day' ? 'eee, MMM d yyyy' : 'eee, MMM d yyy (h:mm aaaa)')

                updatedAlert['date'] = date
                updatedAlert['subtitle'] = subtitle
                updatedAlerts.push(updatedAlert)
            })
        } else if(hasSchedule & updatedItem['alerts'].length === 0) {
            // Create alert
            const date = new Date(refDate)
            const type = date.getHours() === 0 & date.getMinutes() === 0 & date.getSeconds() === 0 & date.getMilliseconds() === 0 ? 'day' : 'time'
            const title = item.getPlainText()
            const subtitle = format(date, type === 'day' ? 'eee, MMM d yyyy' : 'eee, MMM d yyy (h:mm aaaa)')

            let newAlert = new Alert({
                userID: user['uid'],
                title: title,
                subtitle: subtitle,
                date: date,
                secID: {id: item['itemID'], relation: 'item'}
            })
            updatedAlerts.push(newAlert)
        }
        console.log('setSchedule', {alerts: updatedAlerts, item: updatedItem})
        dispatch(actionCreators.updateItemSchedule(updatedAlerts, updatedItem, data, user['uid'], sessionId))
    }

    // Use this to control what will trigger re-render
    let parent
    if(item){
        const parents = Object.values(item['parents'])
        const parentID = parents.length > 0 ? parents.slice(-1)[0] : item['sectionID']
        const parentType = parents.length > 0 ? 'items' : 'sections'
        parent = data[parentType][parentID]
    }
    
    const components = React.useMemo(getComponents, [
        item, 
        item && inputID === `ItemInput_${item['itemID']}`, 
        isDragging, 
        section && section.items['INCOMPLETE'],
        parent
    ]) 
    // const components = getComponents()
    
    if(!item){
        return <div></div>
    } else {
        return (
            <PopOut point element={<ItemOptions item={item}/>}
                trigger={
                    isMobile & !inputID ? 'leftClick' 
                    : `ItemInput_${item['itemID']}` !== inputID & !disableButtons ? 'rightClick' 
                    : null
                }
                >
                {/* <div style={{border: '1px solid var(--secondary-color)', padding: '0.5rem', borderRadius: 5}}>{itemID}</div> */}
                <AnimatePresence>
                    <Animation>
                        <Wrapper>
                            <div className={
                                `ItemInput_${item['itemID']}` === inputID & !['SEARCH','VIEW'].includes(type) & styles !== 'secondary' ? classes.EditBlock
                                : `ItemInput_${item['itemID']}` === inputID & styles === 'secondary' ? classes.EditBlock2
                                : type === 'DRAG_OVERLAY' ? classes.DragOverlay
                                : isDragging ? classes.DragPreview
                                : type === 'KANBAN' ? classes.KanbanBlock
                                : type === 'SEARCH' ? classes.SearchBlock
                                : type === 'VIEW' ? classes.ViewBlock
                                : type === 'VIEW2' ? classes.ViewBlock2
                                : type === 'SECONDARY' ? classes.SecondaryListBlock
                                : classes.ListBlock
                            }>
                                <div className={classes.Container}>
                                    {components['primaryButtons']}
                                    {components['text']}
                                    {!edit && !disableButtons ? components['secondaryButtons'] : null}
                                    {`ItemInput_${item['itemID']}` !== inputID ? <div className={type === 'KANBAN' ? classes.KanbanProperties : classes.Properties}>{components['properties']}</div> : null}
                                </div>
                            </div>
                            {showDivider && <Divider/>}
                        </Wrapper>
                    </Animation>
                </AnimatePresence>
            </PopOut>
        )
    }
}

export default ItemBlock;